// src/careJobs/pages/JobDetailPage.tsx
import React from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Chip, Stack, Typography, Alert } from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_JOB_POSTING} from "../../../../platform/graphql/queries/careJobs";
import ReactMarkdown from "react-markdown"; 
import JobApplyDialog from "../components/jobApplyDialog";
import SeoMeta from "../../../../platform/components/common/seoMeta";

export default function JobDetailPage() {
  const { jobId } = useParams();
  const id = Number(jobId);
  const { data, loading, error } = useQuery(GET_JOB_POSTING, {
    variables: { id },
    skip: !Number.isFinite(id),
    fetchPolicy: "cache-and-network",
  });

  const job = data?.jobPosting || null;
  const [applyOpen, setApplyOpen] = React.useState(false);
  const seoTitle = job?.title
    ? `${job.title}${job?.business?.name ? ` at ${job.business.name}` : ""} | Plany365`
    : "Care Job | Plany365";
  const seoDescription =
    String(job?.description || "").trim() ||
    `View this caregiving job${job?.business?.name ? ` at ${job.business.name}` : ""} on Plany365.`;
  const seoUrl = Number.isFinite(id) ? `/care/jobs/${id}` : "/care/jobs";

  if (!Number.isFinite(id)) {
    return (
      <Box sx={{ maxWidth: 960, mx: "auto", px: 2, py: 4 }}>
        <Alert severity="warning">Invalid job id.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", px: 2, py: 4 }}>
      <SeoMeta title={seoTitle} description={seoDescription} url={seoUrl} />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {String((error as any)?.message || error)}
        </Alert>
      )}
      {loading && !job ? (
        <Typography>Loading…</Typography>
      ) : !job ? (
        <Alert severity="info">Job not found or inactive.</Alert>
      ) : (
        <>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {job.title}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {job.employmentType && <Chip size="small" label={job.employmentType} />}
            {(job.locationCity || job.locationState) && (
              <Chip size="small" label={[job.locationCity, job.locationState].filter(Boolean).join(", ")} />
            )}
            {job.compensationSummary && <Chip size="small" label={job.compensationSummary} />}
            {job.postedAt && (
              <Chip size="small" label={`Posted ${new Date(job.postedAt).toLocaleDateString()}`} />
            )}
          </Stack>

          <Box sx={{ border: "1px solid", borderColor: "divider", p: 2, borderRadius: 1, mb: 2 }}>
            <ReactMarkdown>{job.descriptionMarkdown || "_No description provided._"}</ReactMarkdown>
          </Box>

          <Button variant="contained" onClick={() => setApplyOpen(true)}>
            Apply
          </Button>

          <JobApplyDialog
            open={applyOpen}
            onClose={() => setApplyOpen(false)}
            jobId={job.id}
            jobTitle={job.title}
          />
        </>
      )}
    </Box>
  );
}
